import {
  BalanceWithDeltaCell,
  BenefitAmountCell,
  CombinedSpousesTotalCell,
  CostCell,
  CumulativeDrawnCell,
  LtcPoolWithDeltaCell,
  PaidCell,
  PoolLimitCell,
  WhenCell,
} from './cells';
import {
  getLtcPool,
  getMonthPayout,
} from '@shared/calculations/funding/projection/projectionTotals';
import { BalanceBreakdownTooltip } from './BalanceBreakdownTooltip';
import { claimantMonthKey, currency } from './formatters';
import { GrowthFactor, HeaderTooltip } from './HeaderTooltip';
import type { StrategyPresentation, UniversalColumnKey } from './strategies/types';
import type {
  AggRenderContext,
  ColumnGroup,
  ColumnSpec,
  MonthRenderContext,
  PoolDrawdownToDate,
} from './types';
import type { ReactNode } from 'react';

const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);
const avg = (xs: number[]) => (xs.length === 0 ? 0 : sum(xs) / xs.length);

// Full household pool remaining for the Total row. On joint ShareCare the
// engine surfaces only the row's ACTIVE claimant's drawable capacity (their
// individual ⅓ + the shared ⅓) on `baseBenefitPool`, so `getLtcPool` of the
// last row omits the OTHER spouse's individual ⅓ — summing the three
// `jointPools` sub-pools is the only way to get the true policy-level remaining
// (the value the "both spouses" Total cell claims to show). Every other
// strategy (single-life and the non-ShareCare joint products) shares one pool,
// so `getLtcPool` is already the household total there.
function householdPoolRemaining(state: MonthlyState): number {
  const jointPools = (
    state.strategyState as
      | { jointPools?: { clientIndividual: number; partnerIndividual: number; shared: number } }
      | undefined
  )?.jointPools;
  if (!jointPools) return getLtcPool(state);
  return (
    jointPools.clientIndividual +
    jointPools.partnerIndividual +
    jointPools.shared +
    state.pools.extensionOfBenefitsPool
  );
}

// Generic indemnity surplus reserve. Every indemnity-capable strategy tracks
// the same shape: `state.strategyState.indemnitySurplus` is the running reserve,
// and the monthly deltas carry `indemnitySurplusAccrued` (benefit > cost) and
// `indemnitySurplusApplied` (cost > benefit). Read GENERICALLY here so all
// indemnity products surface the same Surplus balance column — not just CM II.
const surplusBalanceOf = (state: MonthlyState): number =>
  (state.strategyState as { indemnitySurplus?: number }).indemnitySurplus ?? 0;

function surplusCellTooltip(month: MonthlyEntry, openState: MonthlyState): ReactNode | undefined {
  const sd = month.deltas.strategyDeltas as {
    indemnitySurplusAccrued?: number;
    indemnitySurplusApplied?: number;
  };
  const accrued = sd.indemnitySurplusAccrued ?? 0;
  const applied = sd.indemnitySurplusApplied ?? 0;
  const open = surplusBalanceOf(openState);
  const close = surplusBalanceOf(month.state);
  if (open === 0 && close === 0 && accrued === 0 && applied === 0) return undefined;
  return (
    <BalanceBreakdownTooltip
      title="Indemnity surplus"
      description="Indemnity policies pay a fixed monthly benefit regardless of actual cost. Months where the benefit exceeds cost accrue the excess as a surplus reserve, which is drawn down later to cover months where cost exceeds the benefit."
      sections={[
        {
          opening: open,
          closing: close,
          lines: [
            { label: 'Accrued (benefit > cost)', value: accrued, kind: 'add' },
            { label: 'Applied (cost > benefit)', value: applied, kind: 'subtract' },
          ],
        },
      ]}
    />
  );
}

const DEFAULT_POOL_TOOLTIP = (
  <HeaderTooltip
    description="End-of-month LTC pool balance, with this month's change beneath."
    formulas={[
      { label: 'Closing pool', expression: 'opening + inflation accrual − drawn this month' },
      { label: 'Δ shown', expression: 'closing − opening' },
    ]}
  />
);

const DEFAULT_PAYOUT_COLUMN_TOOLTIP = (
  <HeaderTooltip
    description="Policy payout this month, with cumulative dollars paid by the policy through this month in parentheses."
    formulas={[
      { label: 'Headline', expression: "this month's payout" },
      { label: 'Cumulative', expression: 'Σ monthly payouts through this month' },
    ]}
  />
);

// Shared disclaimer surfaced wherever Care cost is displayed. The asterisk on
// the column label points here; per-strategy cost tooltips also reuse this
// note so the meaning is identical everywhere.
export const PROJECTED_COST_NOTE = (
  <>
    <span className="font-semibold text-amber-300">*</span> Care cost is{' '}
    <span className="italic">projected</span>. Waterlily uses statistical growth modeling on your
    demographics, care preferences, and current health to estimate care duration, environment, and
    unit costs. Actual costs may vary.
  </>
);

const DEFAULT_COST_TOOLTIP = (
  <HeaderTooltip
    description="Projected total monthly care cost (with inflated per-day rate beneath). The daily rate comes from the care plan's phase × environment unit cost."
    formulas={[
      {
        label: 'Daily cost',
        expression: (
          <>
            today's daily unit cost ×{' '}
            <GrowthFactor
              rate="care-cost inflation"
              exponent="years from today"
            />
          </>
        ),
      },
      {
        label: 'Monthly cost',
        expression: 'daily cost × days in month',
      },
    ]}
    notes={PROJECTED_COST_NOTE}
  />
);

const DEFAULT_BENEFIT_AMOUNT_TOOLTIP = (
  <HeaderTooltip
    description="Policy monthly benefit maximum for this policy year, with the daily equivalent beneath."
    formulas={[
      {
        label: 'Monthly max',
        expression: (
          <>
            at-issue daily × days in month ×{' '}
            <GrowthFactor
              rate="inflation rate"
              exponent="policy year − 1"
            />
          </>
        ),
      },
    ]}
    notes="Capped at the inflation cap year if the rider specifies one. Simple-interest riders use (1 + rate × years) instead of compounding."
  />
);

const DEFAULT_POOL_LIMIT_TOOLTIP = (
  <HeaderTooltip
    description={
      <>
        Inflated LTC pool size at this policy year if no benefits were used. Pools keep growing on
        each future anniversary until any inflation cap is reached. Distinct from{' '}
        <span className="italic">Pool remaining</span>, which is the actual balance after payouts.
      </>
    }
    formulas={[
      {
        label: 'Per pool',
        expression: (
          <>
            at-issue pool ×{' '}
            <GrowthFactor
              rate="pool inflation rate"
              exponent="policy year − 1"
            />
          </>
        ),
      },
      {
        label: 'Total',
        expression: 'Σ per-pool values (AOB + COB)',
      },
    ]}
    notes="Each pool is capped at its own inflation cap year if specified — once capped, the value is a true ceiling."
  />
);

const DEFAULT_DRAWN_TOOLTIP = (
  <HeaderTooltip
    description="Cumulative reduction of the LTC pool through this month — any utilization before the projection plus Acceleration (AOB) and Continuation (COB) drawdown."
    formulas={[
      { label: 'Drawn', expression: 'prior utilization + Σ (monthly pool depletion)' },
      { label: 'Identity', expression: 'Pool limit − Drawn = Pool remaining (while pool is open)' },
    ]}
    notes="Reflects pool drawdown, which can be less than cash paid (indemnity / rider-on-top)."
  />
);

const DEFAULT_DEATH_BENEFIT_TOOLTIP = (
  <HeaderTooltip
    description="End-of-month death benefit, with this month's change beneath. Closes equal to the next month's opening."
    formulas={[
      {
        label: 'Death benefit',
        expression: 'at-issue Face Amount − Σ (qualifying drawdown)',
      },
      {
        label: 'Floor',
        expression: 'max(above, Residual / minimum guaranteed)',
      },
    ]}
  />
);

/** Column set for the post-payout "After benefits begin" table. */
export function buildPayoutColumnGroups(opts: {
  terms: ClaimTerms;
  yearSnapshots: YearSnapshot[];
  presentation: StrategyPresentation;
  poolDrawdown: PoolDrawdownToDate;
  // Per-claimant relative-month map (joint policies). Omit for single-life;
  // see `computeClaimantRelativeMonthMap`.
  monthDisplayMap?: Map<string, number>;
  // Set when the table is filtered to ONE claimant of a joint policy. The
  // monthly rows above are then per-claimant, but the policy-level running
  // totals are not: pool drawdown / remaining and cumulative policy payout span
  // BOTH spouses. So this flag (a) retitles "Drawn to date (both spouses)" and
  // (b) annotates the Total row's payout + pool-remaining cells, instead of
  // silently presenting a combined total as if it were the selected claimant's
  // (P2-47).
  claimantFilteredJointView?: boolean;
}): ColumnGroup[] {
  const {
    yearSnapshots,
    terms,
    presentation,
    poolDrawdown,
    monthDisplayMap,
    claimantFilteredJointView,
  } = opts;
  const snapshotByYear = new Map(yearSnapshots.map(s => [s.policyYear, s]));
  const labelFor = (key: UniversalColumnKey, fallback: string) =>
    presentation.columnLabels?.[key] ?? fallback;
  // `byRowKey` is built over the FULL projection in row order, so its last
  // entry is the end-of-projection drawdown. The Total row uses it directly —
  // under a one-claimant filter the last VISIBLE row can sit mid-projection,
  // which would understate a policy-level running total.
  const drawnValues = Array.from(poolDrawdown.byRowKey.values());
  const drawnAtEnd = drawnValues[drawnValues.length - 1];

  const universalGroups: ColumnGroup[] = [
    {
      title: 'Timeline',
      columns: [
        {
          key: 'when',
          label: labelFor('when', 'Month of LTC'),
          tooltip: 'Month of LTC, policy year, and client age.',
          sticky: true,
          render: ({ month }) => (
            <WhenCell
              month={month}
              terms={terms}
              monthDisplayMap={monthDisplayMap}
            />
          ),
          // Distinct calendar months, not rows — joint 'both' view interleaves
          // a row per spouse, so 12 overlapping months would otherwise read
          // "24 mo".
          renderAggregate: ({ label, months }) => (
            <div className="leading-tight">
              <div className="text-sm font-semibold text-gray-900">{label}</div>
              <div className="text-[10px] text-gray-500">
                {new Set(months.map(m => m.context.monthIndex)).size} mo
              </div>
            </div>
          ),
        },
      ],
    },
    {
      title: 'This month',
      columns: [
        {
          key: 'cost',
          label: labelFor('cost', 'Care cost'),
          labelSuffix: (
            <sup
              className="ml-0.5 font-normal text-gray-500"
              aria-label="projected"
            >
              *
            </sup>
          ),
          tooltip: presentation.copy?.costTooltip ?? DEFAULT_COST_TOOLTIP,
          blankKey: ({ month }) => month.facts.effectiveCareCost,
          render: ({ month }) => (
            <CostCell
              month={month}
              terms={terms}
            />
          ),
          renderAggregate: ({ months }) => (
            <div className="leading-tight">
              <div className="font-medium text-gray-900">
                {currency(sum(months.map(m => m.facts.effectiveCareCost)))}
              </div>
              <div className="text-[10px] text-gray-500">total</div>
            </div>
          ),
        },
        {
          key: 'benefit-amount',
          label: labelFor('benefit-amount', 'Benefit cap'),
          tooltip: presentation.copy?.benefitAmountTooltip ?? DEFAULT_BENEFIT_AMOUNT_TOOLTIP,
          blankKey: ({ month }) => {
            const snapshot = snapshotByYear.get(month.context.policyYear);
            return (
              presentation.benefitAmountValueForMonth?.({ month, snapshot, terms }) ??
              snapshot?.monthlyBenefitCap ??
              null
            );
          },
          render: ({ month }) => {
            const snap = snapshotByYear.get(month.context.policyYear);
            const strategyCell = presentation.benefitAmountCell?.({
              month,
              snapshot: snap,
              terms,
            });
            if (strategyCell) return strategyCell;
            if (!snap) return <span className="text-gray-300">—</span>;
            return (
              <BenefitAmountCell
                snapshot={snap}
                terms={terms}
                presentation={presentation}
              />
            );
          },
          renderAggregate: ({ months }) => {
            const values = months
              .map(m => {
                const snap = snapshotByYear.get(m.context.policyYear);
                return (
                  presentation.benefitAmountValueForMonth?.({ month: m, snapshot: snap, terms }) ??
                  snap?.monthlyBenefitCap
                );
              })
              .filter((v): v is number => v !== undefined && v !== null);
            if (values.length === 0) return <span className="text-gray-300">—</span>;
            return (
              <div className="leading-tight">
                <div className="font-medium text-gray-900">{currency(avg(values))}</div>
                <div className="text-[10px] text-gray-500">avg/mo</div>
              </div>
            );
          },
        },
      ],
    },
    {
      title: 'This month',
      columns: [
        {
          key: 'payout',
          label: labelFor('payout', 'Policy payout'),
          tooltip: presentation.copy?.payoutColumnTooltip ?? DEFAULT_PAYOUT_COLUMN_TOOLTIP,
          blankKey: ({ month, cumulativeLtcPayoutToDate }) =>
            `${getMonthPayout(month)}:${cumulativeLtcPayoutToDate}`,
          render: ({ month, cumulativeLtcPayoutToDate }) => (
            <PaidCell
              month={month}
              cumulativeToDate={cumulativeLtcPayoutToDate}
              presentation={presentation}
              terms={terms}
            />
          ),
          renderAggregate: ({ cumulativeLtcPayoutAtEnd }) =>
            claimantFilteredJointView ? (
              <CombinedSpousesTotalCell
                value={cumulativeLtcPayoutAtEnd}
                caption="paid to date"
              />
            ) : (
              <div className="leading-tight">
                <div className="font-medium text-gray-900">
                  {currency(cumulativeLtcPayoutAtEnd)}
                </div>
                <div className="text-[10px] text-gray-500">paid to date</div>
              </div>
            ),
        },
      ],
    },
    {
      title: 'Running balances',
      columns: [
        {
          key: 'pool',
          label: labelFor('pool', 'Pool remaining'),
          tooltip:
            (typeof presentation.copy?.poolTooltip === 'function'
              ? presentation.copy.poolTooltip(terms)
              : presentation.copy?.poolTooltip) ?? DEFAULT_POOL_TOOLTIP,
          blankKey: ({ month }) => (terms.isUnlimited ? 'unlimited' : getLtcPool(month.state)),
          render: ({ month, openState }) => (
            <LtcPoolWithDeltaCell
              openState={openState}
              closeState={month.state}
              unlimited={terms.isUnlimited}
              tooltipContent={
                presentation.poolCellTooltip?.({ month, openState, terms }) ?? undefined
              }
            />
          ),
          renderAggregate: ({ closeStateAtEnd }) =>
            claimantFilteredJointView ? (
              <CombinedSpousesTotalCell
                value={householdPoolRemaining(closeStateAtEnd)}
                unlimited={terms.isUnlimited}
              />
            ) : (
              <BalanceWithDeltaCell
                opening={householdPoolRemaining(closeStateAtEnd)}
                closing={householdPoolRemaining(closeStateAtEnd)}
                unlimited={terms.isUnlimited}
              />
            ),
        },
        {
          key: 'pool-limit',
          label: labelFor('pool-limit', 'Pool limit'),
          tooltip: presentation.copy?.poolLimitTooltip ?? DEFAULT_POOL_LIMIT_TOOLTIP,
          render: ({ month }) => {
            const snapshot = snapshotByYear.get(month.context.policyYear);
            return (
              presentation.poolLimitCell?.({ snapshot, terms }) ?? (
                <PoolLimitCell
                  snapshot={snapshot}
                  terms={terms}
                />
              )
            );
          },
          renderAggregate: ({ months }) => {
            const snapshot = snapshotByYear.get(months[months.length - 1]?.context.policyYear);
            return (
              presentation.poolLimitCell?.({ snapshot, terms }) ?? (
                <PoolLimitCell
                  snapshot={snapshot}
                  terms={terms}
                />
              )
            );
          },
        },
        {
          key: 'pool-drawn',
          label: claimantFilteredJointView
            ? `${labelFor('pool-drawn', 'Drawn to date')} (both spouses)`
            : labelFor('pool-drawn', 'Drawn to date'),
          tooltip: claimantFilteredJointView ? (
            <>
              {presentation.copy?.drawnTooltip ?? DEFAULT_DRAWN_TOOLTIP}
              <div className="mt-2 text-[10px] text-gray-300">
                Pool drawdown is policy-level: this running total still includes the other
                spouse's draws while the table is filtered to one claimant.
              </div>
            </>
          ) : (
            presentation.copy?.drawnTooltip ?? DEFAULT_DRAWN_TOOLTIP
          ),
          render: ({ month }) => (
            <CumulativeDrawnCell
              drawn={poolDrawdown.byRowKey.get(
                claimantMonthKey(month.context.claimant, month.context.monthIndex),
              )}
              priorUtilization={poolDrawdown.priorUtilization}
              labels={presentation.drawnCellLabels}
            />
          ),
          renderAggregate: () => (
            <CumulativeDrawnCell
              drawn={drawnAtEnd}
              priorUtilization={poolDrawdown.priorUtilization}
              labels={presentation.drawnCellLabels}
            />
          ),
        },
        {
          key: 'death-benefit',
          label: labelFor('death-benefit', 'Death benefit balance'),
          tooltip: presentation.copy?.deathBenefitTooltip ?? DEFAULT_DEATH_BENEFIT_TOOLTIP,
          blankKey: ({ month }) => month.state.pools.deathBenefit,
          render: ({ month, openState }) => (
            <BalanceWithDeltaCell
              opening={openState.pools.deathBenefit}
              closing={month.state.pools.deathBenefit}
              tooltipContent={
                presentation.deathBenefitCellTooltip?.({ month, openState, terms }) ?? undefined
              }
            />
          ),
          renderAggregate: ({ closeStateAtEnd }) => (
            <BalanceWithDeltaCell
              opening={closeStateAtEnd.pools.deathBenefit}
              closing={closeStateAtEnd.pools.deathBenefit}
            />
          ),
        },
        // Surplus only makes sense on indemnity products — reimbursement
        // products can't accrue a surplus reserve. Shared across every
        // indemnity strategy so the Returns-summary "Indemnity surplus" total
        // is derivable from the visible table.
        ...(terms.isIndemnity
          ? [
              {
                key: 'surplus',
                label: 'Surplus balance',
                tooltip:
                  'End-of-month surplus balance, with this month’s change beneath (+ accrued / − applied).',
                blankKey: ({ month }: MonthRenderContext) => surplusBalanceOf(month.state),
                render: ({ month, openState }: MonthRenderContext) => (
                  <BalanceWithDeltaCell
                    opening={surplusBalanceOf(openState)}
                    closing={surplusBalanceOf(month.state)}
                    tooltipContent={surplusCellTooltip(month, openState)}
                  />
                ),
                renderAggregate: ({ closeStateAtEnd }: AggRenderContext) => (
                  <BalanceWithDeltaCell
                    opening={surplusBalanceOf(closeStateAtEnd)}
                    closing={surplusBalanceOf(closeStateAtEnd)}
                  />
                ),
              },
            ]
          : []),
      ],
    },
  ];

  const extras = presentation.extraPayoutColumnGroups?.({ terms, yearSnapshots }) ?? [];
  const hidden = new Set<string>(presentation.hiddenColumns ?? []);
  if (terms.strategyId === 'membership-program' && terms.isUnlimited) {
    hidden.add('benefit-amount');
    hidden.add('pool');
  }
  // Filter hidden columns *and* drop any column group left empty so we don't
  // render a phantom group separator.
  const filtered = [...universalGroups, ...extras]
    .map(group => ({ ...group, columns: group.columns.filter(c => !hidden.has(c.key)) }))
    .filter(group => group.columns.length > 0);
  return filtered;
}
